import React, { useState } from 'react';
import { Bell, BellRing, ShieldCheck, Package, Truck, X } from 'lucide-react';
import { Button } from '../ui/button';
import { requestPermission, skipPermission, markOnboardingShown } from '../../utils/pushService';
import { toast } from 'sonner';

/**
 * Notification permission onboarding screen.
 * Shows after first login on native apps to explain why notifications are needed
 * before triggering the native OS permission dialog.
 */
const NotificationOnboarding = ({ onComplete, userRole = 'customer' }) => {
  const [loading, setLoading] = useState(false);

  const benefits = userRole === 'delivery' ? [
    { icon: Package, title: 'New Order Alerts', desc: 'Get notified instantly when new deliveries are assigned to you' },
    { icon: Truck, title: 'Route Updates', desc: 'Receive optimized route changes and schedule updates' },
    { icon: Bell, title: 'Important Reminders', desc: 'Never miss shift starts, pickups, or delivery deadlines' },
  ] : [
    { icon: Package, title: 'Order Updates', desc: 'Know when your order is confirmed, packed, and out for delivery' },
    { icon: Truck, title: 'Delivery Tracking', desc: 'Real-time updates when your delivery is on the way' },
    { icon: Bell, title: 'Offers & Reminders', desc: 'Get notified about exclusive deals and subscription reminders' },
  ];

  const handleEnable = async () => {
    setLoading(true);
    try {
      // requestPermission now handles: listeners → register → token sync (in correct order)
      const notifHandler = (n) => {
        if (n.foreground) toast.info(n.title, { description: n.body });
      };
      const result = await requestPermission(notifHandler);
      if (result === 'granted') {
        toast.success('Notifications enabled!');
      } else if (result === 'denied') {
        toast.error('Permission denied. You can enable notifications in Settings.');
      }
    } catch (e) {
      console.error('Permission error:', e);
    } finally {
      setLoading(false);
      onComplete();
    }
  };

  const handleSkip = () => {
    skipPermission();
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[var(--theme-background)] flex flex-col" data-testid="notification-onboarding">
      {/* Skip button — padded for safe area on notched devices */}
      <div className="flex justify-end p-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <button
          onClick={handleSkip}
          className="flex items-center gap-1 text-sm text-[var(--theme-text-muted)] hover:text-[var(--theme-text-main)] transition-colors"
          data-testid="notification-skip-btn"
        >
          Maybe Later <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8 max-w-md mx-auto">
        {/* Icon */}
        <div className="relative mb-8">
          <div className="w-24 h-24 rounded-3xl bg-[var(--theme-primary)]/10 flex items-center justify-center">
            <BellRing className="w-12 h-12 text-[var(--theme-primary)]" />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">!</span>
          </div>
        </div>

        {/* Title */}
        <h2 className="font-heading text-2xl font-bold text-[var(--theme-text-main)] text-center mb-2">
          Stay Updated
        </h2>
        <p className="text-sm text-[var(--theme-text-muted)] text-center mb-8">
          Enable notifications to never miss important updates
        </p>

        {/* Benefits */}
        <div className="w-full space-y-4 mb-10">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-4" data-testid={`notification-benefit-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[var(--theme-primary)]/10 flex items-center justify-center shrink-0">
                <b.icon className="w-5 h-5 text-[var(--theme-primary)]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-[var(--theme-text-main)]">{b.title}</h3>
                <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Privacy note */}
        <div className="flex items-center gap-2 mb-6 px-4">
          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
          <p className="text-[11px] text-[var(--theme-text-muted)]">
            We only send relevant updates. You can disable notifications anytime in Settings.
          </p>
        </div>

        {/* Actions */}
        <Button
          onClick={handleEnable}
          disabled={loading}
          className="w-full h-12 bg-[var(--theme-primary)] text-white rounded-2xl font-semibold text-base"
          data-testid="notification-enable-btn"
        >
          {loading ? 'Enabling...' : 'Enable Notifications'}
        </Button>
      </div>
    </div>
  );
};

export default NotificationOnboarding;
